import { Link } from "react-router-dom";
import { ArrowRight, Check, Phone } from "lucide-react";
import SEO from "../components/SEO";
import EditorialMarquee from "../components/EditorialMarquee";
import StatusBadge from "../components/StatusBadge";
import { FadeUp, MaskedLines, SectionLabel } from "../components/Reveal";
import { IMG, PHONE_DISPLAY, PHONE_TEL, WHATSAPP_URL } from "../data/site";
import WhatsAppIcon from "../components/WhatsAppIcon";

const TYPES = [
    "Ouverture de porte simple claquée",
    "Ouverture de porte blindée claquée",
    "Ouverture de porte simple fermée à clé",
    "Ouverture de porte blindée fermée à clé",
    "Ouverture de porte à la radio",
    "Ouverture de porte fine",
    "Ouverture de porte par crochetage",
    "Ouverture de porte sans casse",
    "Ouverture de porte sans dommage",
    "Ouverture de porte non destructive",
    "Ouverture de porte avec perçage de la serrure",
    "Ouverture de porte avec remplacement de serrure",
    "Ouverture de porte pas cher",
    "Ouverture de porte à prix fixe",
    "Ouverture de porte la nuit",
    "Ouverture de porte le dimanche",
    "Ouverture de porte jours fériés",
];

const OFFERS = [
    { label: "Porte simple claquée", labor: "60€", total: "60,00€ TTC" },
    { label: "Porte simple fermée à clé", labor: "60€", total: "sur devis*" },
    { label: "Porte blindée claquée", labor: "60€", total: "sur devis*" },
    { label: "Changement de serrure", labor: "85€", total: "sur devis*" },
];

const SERVICE_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Ouverture de porte",
    name: "Ouverture de porte Paris",
    provider: {
        "@type": "Locksmith",
        name: "Serrurier Paris",
        telephone: "+33625258619",
        url: "https://www.serrurierparis.paris/",
        image: "https://www.serrurierparis.paris/img/logo-serrurierparis.webp",
    },
    areaServed: ["Paris", "Île-de-France"],
    offers: { "@type": "Offer", price: "60", priceCurrency: "EUR", url: "https://www.serrurierparis.paris/ouverture-de-porte" },
};

const FAQ_OUVERTURE = [
    {
        q: "Combien coûte une ouverture de porte à Paris ?",
        a: "Pour une porte simplement claquée (verrou non engagé), le tarif est de 60 € TTC, main d'œuvre incluse. Pour une porte fermée à clé ou une porte blindée, le prix dépend du type de cylindre et de la technique utilisée : comptez entre 90 € et 250 € TTC selon la complexité. Un devis vous est remis avant toute intervention.",
    },
    {
        q: "Mon serrurier va-t-il abîmer ma porte ou ma serrure ?",
        a: "Dans 95 % des cas de porte claquée, nous utilisons la technique à la carte radio (lame souple), qui ne laisse aucune trace. Pour les serrures fermées à clé, nous tentons d'abord le crochetage non destructif. La destruction n'est envisagée qu'en dernier recours — perçage du cylindre — et vous en êtes toujours informé avec un devis avant toute action.",
    },
    {
        q: "En combien de temps le serrurier arrive-t-il à Paris ?",
        a: "Nos artisans sont positionnés dans plusieurs arrondissements de Paris et dans la petite couronne. Le délai moyen d'intervention est de 25 à 35 minutes. La nuit ou en cas de forte demande, il peut atteindre 45 minutes. Vous recevez une estimation précise lors de votre appel.",
    },
    {
        q: "Que faire si ma clé est cassée dans la serrure ?",
        a: "Ne forcez pas davantage : vous risquez de pousser le fragment plus profondément. Appelez-nous : nous disposons d'extracteurs de clé spécifiques qui permettent de retirer le morceau sans démonter la serrure dans la grande majorité des cas. Si le cylindre est endommagé, nous pouvons le remplacer dans la foulée.",
    },
    {
        q: "L'ouverture de porte est-elle remboursée par l'assurance ?",
        a: "Oui, si vous avez souscrit une garantie « assistance » ou « assistance serrurier » dans votre contrat multirisques habitation. Nous vous remettons systématiquement une facture détaillée conforme aux exigences des assureurs. Certains contrats couvrent jusqu'à 100 % des frais ; vérifiez les plafonds dans vos conditions générales.",
    },
    {
        q: "Intervenez-vous la nuit et les jours fériés ?",
        a: "Oui, 24h/24 et 7j/7, y compris les dimanches et jours fériés. Un supplément de 20 à 30 € peut s'appliquer pour les interventions entre 22h et 7h du matin — il vous est annoncé au téléphone avant que le technicien ne se déplace.",
    },
];

const TECHNIQUES = [
    {
        titre: "Ouverture à la carte / lame souple",
        desc: "Technique privilégiée pour les portes claquées (verrou non engagé). Une lame fine est glissée entre le bâti et le pêne demi-tour, qui recule sans laisser de trace. Durée : 1 à 3 minutes. Aucun dommage sur la serrure ni sur la porte.",
        icon: "✓",
    },
    {
        titre: "Crochetage de serrure",
        desc: "Utilisé pour les cylindres simples ou de milieu de gamme sans protection anti-crochetage. Le serrurier manipule les goupilles du cylindre avec des outils spécifiques (crochet et tension). Efficace sur les serrures sans axe rotatif ni goupilles serpiginées.",
        icon: "✓",
    },
    {
        titre: "Déverrouillage par le judas",
        desc: "Lorsque la clé est laissée côté intérieur, une longue pince-judas ou un câble articulé peut parfois faire tourner la clé depuis l'extérieur. Technique 100 % non destructive, mais applicable seulement si la clé est engagée côté intérieur et que le judas le permet.",
        icon: "✓",
    },
    {
        titre: "Perçage du cylindre",
        desc: "Dernier recours, lorsque toutes les techniques douces ont échoué (serrure haute sécurité A2P BP3, cylindre anti-perçage breveté). Le cylindre est percé et remplacé. Vous en êtes informé et un devis vous est remis avant toute destruction.",
        icon: "!",
    },
];

export default function OuverturePorte() {
    return (
        <div data-testid="ouverture-porte-page">
            <SEO
                title="Ouverture de Porte Paris 60€ TTC | Serrurier Urgence 24/7 — 30 min"
                description="Spécialiste de l'ouverture de porte à Paris : porte claquée 60€ TTC, sans casse. Serrurier agréé assurance, intervention en 30 min, 24h/24. Devis gratuit et transparent. ☎ 06 25 25 86 19"
                path="/ouverture-de-porte"
                schema={SERVICE_SCHEMA}
            />

            {/* HERO */}
            <section className="grain relative overflow-hidden bg-midnight py-10 text-white md:py-16">
                <div className="pointer-events-none absolute -right-24 top-0 h-80 w-80 rounded-full bg-emergency/20 blur-3xl" />
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <nav className="mb-5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/50" data-testid="breadcrumb">
                        <Link to="/" className="transition-colors hover:text-gold">Accueil</Link>
                        <span>/</span>
                        <span className="text-gold">Ouverture de porte</span>
                    </nav>
                    <SectionLabel dark>Service — Paris & Île-de-France</SectionLabel>
                    <StatusBadge className="mt-4" />
                    <h1 className="mt-3 max-w-4xl font-display text-4xl font-extrabold leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl">
                        <MaskedLines delay={0.15} lines={["Ouverture de porte à Paris."]} />
                        <MaskedLines delay={0.45} lines={["60€ TTC."]} lineClassName="text-[#f97316]" />
                    </h1>
                    <FadeUp delay={0.5}>
                        <p className="mt-6 max-w-xl leading-relaxed text-white/70">
                            Porte claquée, bloquée ou fermée à clé de l'intérieur : l'ouverture de porte sans casse
                            est notre spécialité. Artisan serrurier depuis 1985, agréé assurance, chez vous en
                            25 minutes.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-4">
                            <a
                                href={PHONE_TEL}
                                data-testid="service-call-button"
                                className="flex items-center gap-3 bg-emergency px-7 py-4 font-bold text-white transition-colors hover:bg-emergency-dark"
                            >
                                <Phone className="h-5 w-5" /> {PHONE_DISPLAY}
                            </a>
                            <a
                                href={WHATSAPP_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="service-whatsapp-button"
                                className="flex items-center gap-3 border border-white/30 px-7 py-4 font-bold text-white transition-colors hover:border-[#25D366] hover:bg-[#25D366]"
                            >
                                <WhatsAppIcon className="h-5 w-5" /> WhatsApp
                            </a>
                        </div>
                    </FadeUp>
                </div>
            </section>

            <EditorialMarquee dark={false} />

            {/* TYPES */}
            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
                <div className="grid gap-12 lg:grid-cols-[1fr_1.5fr] lg:gap-20">
                    <FadeUp>
                        <SectionLabel>Toutes situations</SectionLabel>
                        <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                            Ouverture de porte claquée, bloquée ou fermée à clé
                        </h2>
                        <p className="mt-6 leading-relaxed text-midnight/70">
                            Notre équipe intervient sur toute la zone parisienne. Selon la problématique, différentes
                            techniques d'ouverture sont praticables — de la plus simple à la plus destructive, en
                            privilégiant toujours l'ouverture sans casse.
                        </p>
                    </FadeUp>
                    <div className="grid gap-px bg-midnight/10 sm:grid-cols-2" data-testid="opening-types-grid">
                        {TYPES.map((t, i) => (
                            <FadeUp key={t} delay={(i % 6) * 0.04} className="bg-white">
                                <div className="flex h-full items-center gap-3 px-5 py-4 text-sm font-semibold text-midnight/80">
                                    <Check className="h-4 w-4 shrink-0 text-emergency" /> {t}
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* DEUX CAS */}
            <section className="bg-white py-16 md:py-24">
                <div className="mx-auto max-w-7xl space-y-20 px-4 sm:px-6 lg:px-8">
                    <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
                        <FadeUp>
                            <img
                                src={IMG("ouverture-porte.webp")}
                                alt="Ouverture de porte fermée à clé à Paris"
                                className="aspect-[4/3] w-full rounded-xl object-cover shadow-[0_24px_60px_-20px_rgba(10,17,40,0.28)]"
                                width="800"
                                height="600"
                                loading="lazy"
                            />
                        </FadeUp>
                        <div>
                            <FadeUp>
                                <span className="font-display text-5xl font-extrabold text-midnight/15">01</span>
                                <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight md:text-4xl">
                                    Votre porte est fermée à clé de l'intérieur
                                </h2>
                                <p className="mt-5 leading-relaxed text-midnight/70">
                                    L'intervention est ici un peu plus complexe. Si vous disposez d'une serrure
                                    d'entrée de gamme, le crochetage de serrure sera la solution idéale pour ne pas
                                    abîmer la porte. Le problème : aujourd'hui, la plupart des portes sont conçues
                                    « anti-crochetage », « anti-perçage », « anti-sciage ».
                                </p>
                                <p className="mt-4 leading-relaxed text-midnight/70">
                                    Les serrures sont de plus en plus résistantes, surtout dans les grandes villes
                                    comme Paris. S'il n'est pas possible d'ouvrir la porte de manière douce, votre
                                    serrurier sera obligé de casser la serrure pour débloquer la porte — en vous
                                    remettant un devis avant toute action.
                                </p>
                            </FadeUp>
                        </div>
                    </div>

                    <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
                        <div className="lg:order-2">
                            <FadeUp>
                                <img
                                    src={IMG("tarif-serrurier.webp")}
                                    alt="Ouverture de porte claquée à la radio — tarif serrurier Paris"
                                    className="aspect-[4/3] w-full rounded-xl object-cover shadow-[0_24px_60px_-20px_rgba(10,17,40,0.28)]"
                                    width="800"
                                    height="600"
                                    loading="lazy"
                                />
                            </FadeUp>
                        </div>
                        <div className="lg:order-1">
                            <FadeUp>
                                <span className="font-display text-5xl font-extrabold text-midnight/15">02</span>
                                <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight md:text-4xl">
                                    Votre porte est simplement claquée
                                </h2>
                                <p className="mt-5 leading-relaxed text-midnight/70">
                                    Pour un serrurier équipé, une porte simplement claquée après un oubli des clés à
                                    l'intérieur du logement n'a rien de compliqué : un simple cliché de radio suffit
                                    dans la plupart des cas.
                                </p>
                                <p className="mt-4 leading-relaxed text-midnight/70">
                                    Sur Paris, nos serruriers estiment que le temps d'intervention pour une porte
                                    claquée ne dépasse pas 5 minutes une fois sur place. Tarif unique : 60€ TTC,
                                    annoncé avant l'intervention.
                                </p>
                            </FadeUp>
                        </div>
                    </div>
                </div>
            </section>

            {/* OFFRES */}
            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
                <FadeUp>
                    <SectionLabel>Nos offres</SectionLabel>
                    <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                        Tarifs ouverture de porte Paris
                    </h2>
                </FadeUp>
                <FadeUp delay={0.1}>
                    <div className="mt-10 overflow-x-auto border border-midnight/10 bg-white" data-testid="offers-table">
                        <table className="w-full min-w-[560px] text-left">
                            <thead>
                                <tr className="bg-midnight text-white">
                                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider">Ouverture de porte</th>
                                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider">Main d'œuvre</th>
                                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider">Total</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-midnight/10">
                                {OFFERS.map((o) => (
                                    <tr key={o.label} className="transition-colors hover:bg-cloud">
                                        <td className="px-6 py-5 font-semibold">{o.label}</td>
                                        <td className="px-6 py-5 text-midnight/70">{o.labor}</td>
                                        <td className="px-6 py-5 font-display text-xl font-extrabold text-[#f97316]">{o.total}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p className="mt-4 text-sm text-midnight/50">
                        * Obtenez un <Link to="/devis-contact" className="font-semibold text-emergency underline">devis gratuit</Link> ou
                        appelez-nous au <a href={PHONE_TEL} className="font-semibold text-emergency underline">{PHONE_DISPLAY}</a>.
                    </p>
                </FadeUp>

                <FadeUp delay={0.15}>
                    <div className="mt-12 border-l-2 border-gold bg-white p-8 md:p-10" data-testid="lock-change-note">
                        <h3 className="font-display text-2xl font-extrabold tracking-tight">
                            Changement de serrure en cours d'intervention
                        </h3>
                        <p className="mt-4 leading-relaxed text-midnight/70">
                            Si le serrurier se rend compte lors de l'intervention qu'il devra changer la serrure, il
                            pourra vous proposer de la remplacer juste après avoir débloqué la porte — notamment si la
                            serrure a dû être détruite car trop résistante. Le prix du changement vous est toujours
                            annoncé et validé avant la pose.
                        </p>
                        <Link
                            to="/changement-de-serrure"
                            className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-emergency"
                        >
                            Voir nos tarifs changement de serrure <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </FadeUp>
            </section>

            {/* TECHNIQUES D'OUVERTURE */}
            <section className="bg-cloud py-16 md:py-24">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <FadeUp>
                        <SectionLabel>Savoir-faire</SectionLabel>
                        <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                            Techniques d'ouverture de porte
                        </h2>
                        <p className="mt-6 max-w-3xl leading-relaxed text-midnight/70">
                            Un serrurier professionnel ne commence jamais par la destruction. Notre protocole d'intervention
                            suit un ordre précis, du moins invasif au plus radical, pour préserver votre porte et votre serrure
                            autant que possible.
                        </p>
                    </FadeUp>
                    <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                        {TECHNIQUES.map((t, i) => (
                            <FadeUp key={t.titre} delay={i * 0.07}>
                                <div className="h-full border border-midnight/10 bg-white p-6">
                                    <span className={`inline-flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold ${t.icon === "!" ? "bg-emergency/10 text-emergency" : "bg-emerald-100 text-emerald-700"}`}>
                                        {t.icon === "!" ? "!" : i + 1}
                                    </span>
                                    <h3 className="mt-4 font-display text-lg font-extrabold tracking-tight">
                                        {t.titre}
                                    </h3>
                                    <p className="mt-3 text-sm leading-relaxed text-midnight/65">{t.desc}</p>
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                    <FadeUp delay={0.3}>
                        <div className="mt-12 grid gap-8 lg:grid-cols-2">
                            <div className="bg-white p-8 border border-midnight/10">
                                <h3 className="font-display text-xl font-extrabold tracking-tight">
                                    Cylindres haute sécurité : ce qu'il faut savoir
                                </h3>
                                <p className="mt-4 leading-relaxed text-midnight/70">
                                    Les cylindres certifiés <strong>A2P BP1, BP2 et BP3</strong> (norme NF EN 1303 et attestation CNPP)
                                    résistent respectivement à 10, 20 et 30 minutes de tentative d'effraction. La résistance
                                    au crochetage, au perçage et à l'arrachage est testée par un laboratoire indépendant.
                                </p>
                                <p className="mt-3 leading-relaxed text-midnight/70">
                                    Sur une porte d'entrée standard parisienne équipée d'un cylindre A2P BP1 (marques Vachette Radial NT,
                                    Fichet F3D ou Mul-T-Lock MT5+), notre serrurier peut généralement ouvrir par manipulation
                                    sans destruction, à condition que la porte soit seulement claquée. Si la clé a été tournée
                                    et qu'un pêne dormant est engagé, le perçage devient inévitable.
                                </p>
                                <p className="mt-3 leading-relaxed text-midnight/70">
                                    Les cylindres A2P BP2 et BP3 (Fichet Vertibar, Abloy Protec2, Evva 3KS) nécessitent
                                    presque systématiquement le perçage car ils intègrent des billes anti-crochetage, des
                                    goupilles serpiginées et parfois un axe rotatif qui rend toute manipulation impossible.
                                    Dans ce cas, le cylindre est remplacé à l'identique ou par un modèle de même niveau.
                                </p>
                            </div>
                            <div className="bg-white p-8 border border-midnight/10">
                                <h3 className="font-display text-xl font-extrabold tracking-tight">
                                    Cas particuliers fréquents à Paris
                                </h3>
                                <ul className="mt-4 space-y-4 text-midnight/70">
                                    <li className="flex gap-3">
                                        <Check className="mt-1 h-4 w-4 shrink-0 text-emergency" />
                                        <div>
                                            <strong className="block text-midnight">Clé cassée dans la serrure</strong>
                                            Nous utilisons des extracteurs à crochet et des extracteurs hélicoïdaux. Le fragment
                                            est retiré sans démonter le cylindre dans 80 % des cas.
                                        </div>
                                    </li>
                                    <li className="flex gap-3">
                                        <Check className="mt-1 h-4 w-4 shrink-0 text-emergency" />
                                        <div>
                                            <strong className="block text-midnight">Serrure bloquée après tentative d'effraction</strong>
                                            Un cambrioleur qui a tenté de forcer la serrure peut avoir endommagé le cylindre.
                                            Nous ouvrons et remplaçons le cylindre dans la même intervention.
                                        </div>
                                    </li>
                                    <li className="flex gap-3">
                                        <Check className="mt-1 h-4 w-4 shrink-0 text-emergency" />
                                        <div>
                                            <strong className="block text-midnight">Porte gonflée (humidité, chaleur)</strong>
                                            Le bois de l'huisserie peut gonfler en été ou après dégât des eaux. Le serrurier
                                            utilise un outil de déformation contrôlée pour élargir légèrement l'encadrement et
                                            libérer le pêne dormant.
                                        </div>
                                    </li>
                                    <li className="flex gap-3">
                                        <Check className="mt-1 h-4 w-4 shrink-0 text-emergency" />
                                        <div>
                                            <strong className="block text-midnight">Serrure trois points bloquée</strong>
                                            Les portes blindées avec condamnation automatique triple sont ouvrables par
                                            manipulation sur la plupart des cylindres de marque Fichet ou Bricard
                                            si la porte est claquée.
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* DÉLAIS & ZONES */}
            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-20 lg:px-8">
                <FadeUp>
                    <SectionLabel>Disponibilité</SectionLabel>
                    <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                        Délais d'intervention à Paris
                    </h2>
                </FadeUp>
                <div className="mt-10 grid gap-6 sm:grid-cols-3">
                    <FadeUp delay={0.05}>
                        <div className="border border-midnight/10 bg-white p-8 text-center">
                            <p className="font-display text-6xl font-extrabold text-[#f97316]">25'</p>
                            <p className="mt-2 font-bold uppercase tracking-wider text-xs text-midnight/50">Délai moyen Paris intra-muros</p>
                            <p className="mt-3 text-sm leading-relaxed text-midnight/65">
                                Nos techniciens sont répartis dans plusieurs arrondissements pour garantir
                                une arrivée rapide, de nuit comme de jour.
                            </p>
                        </div>
                    </FadeUp>
                    <FadeUp delay={0.1}>
                        <div className="border border-midnight/10 bg-white p-8 text-center">
                            <p className="font-display text-6xl font-extrabold text-[#f97316]">24/7</p>
                            <p className="mt-2 font-bold uppercase tracking-wider text-xs text-midnight/50">Disponibilité permanente</p>
                            <p className="mt-3 text-sm leading-relaxed text-midnight/65">
                                Nuits, week-ends, dimanches et jours fériés (1er janvier, 14 juillet,
                                Noël…) — sans frais de déplacement supplémentaires.
                            </p>
                        </div>
                    </FadeUp>
                    <FadeUp delay={0.15}>
                        <div className="border border-midnight/10 bg-white p-8 text-center">
                            <p className="font-display text-6xl font-extrabold text-[#f97316]">20</p>
                            <p className="mt-2 font-bold uppercase tracking-wider text-xs text-midnight/50">Arrondissements couverts</p>
                            <p className="mt-3 text-sm leading-relaxed text-midnight/65">
                                Du 1er au 20ème arrondissement, plus Boulogne-Billancourt, Vincennes,
                                Montreuil, Saint-Denis et l'ensemble de la petite couronne.
                            </p>
                        </div>
                    </FadeUp>
                </div>
                <FadeUp delay={0.2}>
                    <p className="mt-8 leading-relaxed text-midnight/70 max-w-3xl">
                        Lorsque vous appelez, nous vous confirmons immédiatement le délai estimé en fonction
                        de la position de nos techniciens et du trafic. Si vous êtes dans l'impossibilité
                        d'attendre sur le palier (nuit d'hiver, enfant enfermé, personne âgée), nous vous
                        guidons pour rester en sécurité en attendant notre arrivée.
                    </p>
                </FadeUp>
            </section>

            {/* FAQ OUVERTURE */}
            <section className="bg-cloud py-16 md:py-24">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <FadeUp>
                        <SectionLabel>Questions fréquentes</SectionLabel>
                        <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                            FAQ — Ouverture de porte Paris
                        </h2>
                    </FadeUp>
                    <div className="mt-10 space-y-4">
                        {FAQ_OUVERTURE.map((item, i) => (
                            <FadeUp key={item.q} delay={i * 0.06}>
                                <details className="group border border-midnight/10 bg-white">
                                    <summary className="flex cursor-pointer items-center justify-between px-7 py-5 font-display text-lg font-bold tracking-tight">
                                        {item.q}
                                        <ArrowRight className="h-4 w-4 shrink-0 rotate-90 transition-transform group-open:rotate-[270deg]" />
                                    </summary>
                                    <div className="px-7 pb-6 pt-1 leading-relaxed text-midnight/70">{item.a}</div>
                                </details>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>


            {/* CTA */}
            <section className="grain bg-midnight py-16 text-white md:py-20">
                <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-4 sm:px-6 lg:px-8">
                    <div>
                        <h2 className="font-display text-3xl font-extrabold tracking-tight md:text-4xl">
                            Porte bloquée ? Intervention en 30 minutes.
                        </h2>
                        <p className="mt-3 text-white/60">24h/24 — 7j/7, même les jours fériés. Devis gratuit au téléphone.</p>
                    </div>
                    <a
                        href={PHONE_TEL}
                        data-testid="service-bottom-call-button"
                        className="flex items-center gap-3 bg-emergency px-8 py-4 text-lg font-bold text-white transition-colors hover:bg-emergency-dark"
                    >
                        <Phone className="h-5 w-5" /> {PHONE_DISPLAY}
                    </a>
                </div>
            </section>
        </div>
    );
}
